import { HStack, Text, VStack } from '@chakra-ui/react';
import React, { useCallback } from 'react';

import { finleyAssert } from '@finley-technologies/common';

import { CheckConstraint } from './CheckConstraint';
import { CurrencyInput } from './CurrencyInput';
import {
  getFailedConstraintsMessages,
  GlobalCheckConstraint,
} from './GlobalConstraints';
import { OptionalClassWrapper } from './Shared';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { percentageToPercentageLabel } from '~/packages/common-frontend/utils';
import {
  useFundingRequestTemplateContextV1,
  DrawRequest,
} from '~/packages/funding-requests/hooks/useFundingRequestTemplateContextV1';

type DrawRequestProps = {
  drawRequest: DrawRequest;
};

const ClassedDrawRequestAmount = ({
  drawRequest: {
    key,
    requestAmount,
    constraints: {
      minimum: minimumConstraint,
      maximum: maximumConstraint,
      increment: incrementConstraint,
    },
    loan,
  },
}: DrawRequestProps) => {
  const { context } = useFundingRequestTemplateContextV1();

  finleyAssert(loan, 'Loan attributes must be provided for draw request');

  const {
    benchmark: { rate: benchmarkRate },
    waApplicableMarginRate,
    allInRate,
  } = loan.pricing;

  const { user } = useUserAuthOrThrow();
  // @VLYSLS @jendevelops conditionally hide borrowing base dependent values if no cfs w/ borrowing base
  const isValleySls = user.organization.slug === 'valleyBankSls';

  const updateState = useCallback(
    (newValue?: number) => {
      if (context.allowedActions === 'mutable') {
        // @jendevelops TODO remove number check once can have undefined values on the set state for funding.FundingRequest
        if (typeof newValue === 'number') {
          context.setValue({
            type: 'setDrawRequests',
            options: {
              key,
              amount: newValue,
            },
          });
        }
      }
    },
    [key, context],
  );

  return (
    <VStack
      w="100%"
      alignItems="flex-start"
      id="classed-draw-request-amount-stack"
    >
      <CurrencyInput
        state={requestAmount}
        updateState={updateState}
        isDisabled={context.allowedActions === 'readOnly'}
      />
      <HStack alignSelf="flex-end" spacing="24px">
        {/* @jendevelops constraints TODO */}
        <CheckConstraint
          type="minimum"
          value={requestAmount}
          constraintValue={minimumConstraint}
        />
        {incrementConstraint && (
          <CheckConstraint
            type="increment"
            value={requestAmount}
            constraintValue={incrementConstraint}
          />
        )}
        <CheckConstraint
          type="maximum"
          value={requestAmount}
          constraintValue={maximumConstraint}
        />
      </HStack>
      {/* @jendevelops TODO - need to find a better way to hide loan related values if no loan mechanics defined */}
      {isValleySls && (
        <VStack w="100%" alignItems="flex-start" mt="24px">
          <HStack justify="space-between" w="100%">
            <Text fontWeight="600">All-in-Rate</Text>
            <Text fontWeight="600">
              {percentageToPercentageLabel(allInRate * 100, 5, true)}
            </Text>
          </HStack>
          <HStack justify="space-between" w="100%">
            <Text ml="5em" color="black.70">
              Benchmark Rate
            </Text>
            <Text color="black.70">
              {percentageToPercentageLabel(benchmarkRate * 100, 5, true)}
            </Text>
          </HStack>
          <HStack justify="space-between" w="100%">
            <Text ml="5em" color="black.70">
              WA Applicable Margin
            </Text>
            <Text color="black.70">
              {percentageToPercentageLabel(
                waApplicableMarginRate * 100,
                5,
                true,
              )}
            </Text>
          </HStack>
        </VStack>
      )}
    </VStack>
  );
};

export const ClassedDrawRequests = () => {
  const {
    context: {
      data: { drawRequests },
    },
  } = useFundingRequestTemplateContextV1();

  const isClassed = drawRequests.length > 1;

  return (
    <VStack spacing="24px" w="100%" id="classed-draw-requests-stack">
      {drawRequests.map((drawRequest) => (
        <OptionalClassWrapper
          isClassed={isClassed}
          className={drawRequest.name}
          key={drawRequest.key}
        >
          <ClassedDrawRequestAmount drawRequest={drawRequest} />
        </OptionalClassWrapper>
      ))}
    </VStack>
  );
};

/**
 * THIS IS LIVE IN PROD DO NOT UPDATE BEFORE TALKING TO @jendevelops
 */
export const ClassedDrawRequestAmountForMultiClass = ({
  isDisabled,
  drawRequest: {
    key,
    requestAmount,
    constraints: { maximum: maximumConstraint, increment: incrementConstraint },
  },
}: {
  drawRequest: DrawRequest;
  isDisabled: boolean;
}) => {
  const { context } = useFundingRequestTemplateContextV1();

  const updateState = useCallback(
    (newValue?: number) => {
      if (context.allowedActions === 'mutable') {
        // @jendevelops TODO remove number check once can have undefined values on the set state for funding.FundingRequest
        if (typeof newValue === 'number') {
          context.setValue({
            type: 'setDrawRequests',
            options: {
              key,
              amount: newValue,
            },
          });
        }
      }
    },
    [key, context],
  );

  return (
    <VStack
      w="100%"
      alignItems="flex-start"
      id="classed-draw-request-amount-multi-class-stack"
    >
      <CurrencyInput
        state={requestAmount}
        updateState={updateState}
        isDisabled={context.allowedActions === 'readOnly' || isDisabled}
      />
      <HStack alignSelf="flex-end" spacing="24px">
        {/* @jendevelops constraints TODO */}
        {/* restore one we have class specific minimum */}
        {/* <CheckConstraint
          type="minimum"
          value={requestAmount}
          constraintValue={minimumConstraint}
        /> */}
        {incrementConstraint && (
          <CheckConstraint
            type="increment"
            value={requestAmount}
            constraintValue={incrementConstraint}
          />
        )}
        <CheckConstraint
          type="maximum"
          value={requestAmount}
          constraintValue={maximumConstraint}
        />
      </HStack>
      {/* <VStack w="100%" alignItems="flex-start" mt="24px">
        <HStack justify="space-between" w="100%">
          <Text fontWeight="600">All-in-Rate</Text>
          <Text fontWeight="600">
            {percentageToPercentageLabel(allInRate, 4)}
          </Text>
        </HStack>
        <HStack justify="space-between" w="100%">
          <Text ml="5em" color="black.70">
            Benchmark Rate
          </Text>
          <Text color="black.70">
            {percentageToPercentageLabel(benchmarkRate, 4)}
          </Text>
        </HStack>
        <HStack justify="space-between" w="100%">
          <Text ml="5em" color="black.70">
            WA Applicable Margin
          </Text>
          <Text color="black.70">
            {percentageToPercentageLabel(waApplicableMarginRate, 4)}
          </Text>
        </HStack>
      </VStack> */}
    </VStack>
  );
};

/**
 * THIS IS LIVE IN PROD DO NOT UPDATE BEFORE TALKING TO @jendevelops
 */
export const ClassedDrawRequestsForMultiClass = () => {
  const { user } = useUserAuthOrThrow();
  const { context } = useFundingRequestTemplateContextV1();

  const updateState = useCallback(
    (newValue?: number) => {
      if (context.allowedActions === 'mutable') {
        // @jendevelops TODO remove number check once can have undefined values on the set state for funding.FundingRequest
        if (typeof newValue === 'number') {
          context.setValue({
            type: 'setDrawRequests',
            options: {
              // key,
              amount: newValue,
            },
          });
        }
      }
    },
    [context],
  );

  const {
    allowedActions,
    data: { drawRequests, expectedSubmission },
  } = context;

  const isClassed = drawRequests.length > 1;

  const totalDrawRequestAmount = expectedSubmission.draw.global.requestAmount;

  const isGlobalDrawDisabled =
    allowedActions === 'readOnly' ||
    user.organization.slug === 'tamara' ||
    (user.organization.slug === 'parafin' &&
      getFailedConstraintsMessages(expectedSubmission.draw.global.constraints)
        .length > 0);

  const isClassedDrawInputDisabled = user.organization.slug === 'parafin';

  return (
    <VStack
      spacing="24px"
      w="100%"
      id="classed-draw-requests-multi-class-stack"
    >
      <OptionalClassWrapper isClassed={isClassed} className="Total Draw Amount">
        <VStack w="100%" alignItems="flex-end">
          <CurrencyInput
            state={totalDrawRequestAmount}
            updateState={updateState}
            isDisabled={isGlobalDrawDisabled}
          />
          <HStack alignSelf="flex-end" spacing="24px">
            {/* @jendevelops constraints TODO */}
            {user.organization.slug === 'tamara' && (
              <CheckConstraint
                type="minimum"
                value={totalDrawRequestAmount}
                constraintValue={drawRequests[0].constraints.minimum}
              />
            )}
            {user.organization.slug === 'parafin' &&
              expectedSubmission.draw.global.constraints.map((constraint) => (
                <GlobalCheckConstraint
                  constraint={constraint}
                  key={constraint.label}
                />
              ))}
          </HStack>
        </VStack>
      </OptionalClassWrapper>

      {drawRequests.map((drawRequest) => (
        <OptionalClassWrapper
          isClassed={isClassed}
          className={drawRequest.name}
          key={drawRequest.key}
        >
          <ClassedDrawRequestAmountForMultiClass
            drawRequest={drawRequest}
            isDisabled={isClassedDrawInputDisabled}
          />
        </OptionalClassWrapper>
      ))}
    </VStack>
  );
};
